import { t } from '@lingui/macro'
import { useSnackbar } from 'notistack'
import { SubmitHandler, useForm } from 'react-hook-form'
import { z } from 'zod'

import { useUpdateConsultationSettingsMutation } from '@/core/services/consultation'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect } from 'react'

const schema = z.object({
  automaticCloseHours: z.number(),
})

export type ConsultationSettingsFormData = z.infer<typeof schema>

type UseConsultationSettingsFormParams = {
  initialValues?: ConsultationSettingsFormData
}

export function useConsultationSettingsForm({ initialValues }: UseConsultationSettingsFormParams) {
  const { enqueueSnackbar } = useSnackbar()

  const [updateSettings, { isLoading: isSubmitting }] = useUpdateConsultationSettingsMutation()

  const rhForm = useForm<ConsultationSettingsFormData>({
    defaultValues: initialValues ?? { automaticCloseHours: 2 },
    resolver: zodResolver(schema),
  })
  const { reset } = rhForm

  useEffect(() => {
    reset(initialValues)
  }, [initialValues, reset])

  const submitHandler: SubmitHandler<ConsultationSettingsFormData> = async (data) => {
    try {
      await updateSettings({ automaticCloseHours: data.automaticCloseHours }).unwrap()
      enqueueSnackbar(t`Consultation settings updated successfully`, { variant: 'success' })
    } catch (error) {
      enqueueSnackbar(t`Unable to update consultation settings`, { variant: 'error' })
    }
  }

  return { isSubmitting, rhForm, submitHandler }
}
